import { useEffect, useState } from 'react';
import liff from '@line/liff';
import axios from 'axios';
import HomePage from './pages/HomePage';
import PackagePage from './pages/PackagePage';
import CreditsPage from './pages/CreditsPage';
import HistoryPage from './pages/HistoryPage';
import TopupPage from './pages/TopupPage';
import { ModalProvider } from './components/Modal';
import ProfileGate from './components/ProfileGate';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const IS_DEV = process.env.NODE_ENV === 'development';

const PLATFORM_ICONS = {
  Instagram: 'ti-brand-instagram',
  Facebook: 'ti-brand-facebook',
  TikTok: 'ti-brand-tiktok',
  YouTube: 'ti-brand-youtube',
  LINE: 'ti-brand-line',
};

// อ่าน platform จาก URL ทันที ไม่ต้องรอ LIFF
function getPlatformFromURL() {
  const params = new URLSearchParams(window.location.search);
  let p = params.get('platform');
  if (!p) {
    const liffState = params.get('liff.state');
    if (liffState) {
      const decoded = decodeURIComponent(liffState);
      const stateParams = new URLSearchParams(decoded.startsWith('?') ? decoded.slice(1) : decoded);
      p = stateParams.get('platform');
    }
  }
  return p;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!getPlatformFromURL()); // ถ้ามี platform จาก URL → ไม่ต้อง loading
  const [blocked, setBlocked] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [activePage, setActivePage] = useState('home');
  const [dark, setDark] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showTopup, setShowTopup] = useState(false);
  const [platform, setPlatform] = useState(getPlatformFromURL());
  const [platformOptions, setPlatformOptions] = useState([]);

  useEffect(() => {
    liff.init({ liffId: process.env.REACT_APP_LIFF_ID })
      .then(async () => {
        let currentUser;
        const isRealLiff = liff.isInClient() || liff.isLoggedIn();

        if (!isRealLiff) {
          if (IS_DEV) {
            currentUser = {
              userId: 'test-user-001',
              displayName: 'Test User',
              pictureUrl: 'https://via.placeholder.com/100'
            };
          } else {
            setBlocked(true);
            setLoading(false);
            return;
          }
        } else {
          currentUser = await liff.getProfile();
        }

        setUser(currentUser);

        const apiCalls = [
          axios.get(`${BACKEND}/check-profile`, { params: { lineUserId: currentUser.userId } }),
          axios.post(`${BACKEND}/save-profile`, { lineUserId: currentUser.userId, displayName: currentUser.displayName }),
        ];

        if (!platform) {
          apiCalls.push(axios.get(`${BACKEND}/platforms`));
        }

        const results = await Promise.allSettled(apiCalls);

        setNeedsProfile(results[0].status === 'fulfilled' ? !results[0].value.data.hasPhone : false);
        if (!platform && results[2]?.status === 'fulfilled') {
          setPlatformOptions(results[2].value.data || []);
        }

        setLoading(false);
      })
      .catch(err => {
        console.error('LIFF init error:', err);
        setBlocked(true);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0d0d0d'}}>
        <div style={{textAlign:'center'}}>
          <i className="ti ti-flame" style={{fontSize:'40px',color:'#FF4500',display:'block',marginBottom:'12px'}} aria-hidden="true"/>
          <p style={{color:'#555',fontFamily:'Prompt,sans-serif',fontSize:'14px'}}>กำลังโหลดค่ะ...</p>
        </div>
      </div>
    );
  }

  if (blocked) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0d0d0d',padding:'24px',fontFamily:'Prompt,sans-serif'}}>
        <div style={{textAlign:'center'}}>
          <i className="ti ti-lock" style={{fontSize:'40px',color:'#FF4500',display:'block',marginBottom:'14px'}} aria-hidden="true"/>
          <p style={{color:'#eee',fontSize:'15px',fontWeight:'700',marginBottom:'6px'}}>กรุณาเปิดผ่าน LINE OA ค่ะ</p>
          <p style={{color:'#888',fontSize:'12px',lineHeight:1.6}}>หน้านี้ใช้งานได้เฉพาะผ่านเมนูใน LINE Official Account เท่านั้นค่ะ</p>
        </div>
      </div>
    );
  }

  if (!platform) {
    return (
      <div style={{minHeight:'100vh',background:dark?'#0d0d0d':'#f8f8f8',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'24px',fontFamily:'Prompt,sans-serif'}}>
        <i className="ti ti-flame" style={{fontSize:'36px',color:'#FF4500',marginBottom:'10px'}} aria-hidden="true"/>
        <p style={{color:dark?'#eee':'#111',fontSize:'15px',fontWeight:'700',marginBottom:'4px'}}>เลือกแพลตฟอร์มค่ะ</p>
        <p style={{color:dark?'#888':'#666',fontSize:'11.5px',marginBottom:'20px'}}>เลือกบริการที่ต้องการใช้งานค่ะ</p>
        <div style={{display:'flex',flexDirection:'column',gap:'10px',width:'100%',maxWidth:'280px'}}>
          {platformOptions.map(p => (
            <button
              key={p.id}
              onClick={() => setPlatform(p.name)}
              style={{
                display:'flex',alignItems:'center',gap:'10px',padding:'14px 16px',
                borderRadius:'12px',border:`1px solid ${dark?'#2e2e2e':'#e8e8e8'}`,
                background:dark?'#1c1c1c':'#fff',color:dark?'#eee':'#111',
                fontSize:'13px',fontWeight:'600',cursor:'pointer',fontFamily:'Prompt,sans-serif'
              }}
            >
              <i className={`ti ${PLATFORM_ICONS[p.name] || 'ti-apps'}`} style={{fontSize:'20px',color:'#FF4500'}} aria-hidden="true"/>
              {p.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const navItems = [
    { id:'home', icon:'ti-shopping-cart', label:'บริการ' },
    { id:'credits', icon:'ti-wallet', label:'เครดิต' },
    { id:'history', icon:'ti-clock', label:'ประวัติ' },
  ];

  const renderPage = () => {
    if (activePage === 'home' && selectedService) {
      return <PackagePage user={user} dark={dark} service={selectedService} onBack={() => setSelectedService(null)}/>;
    }
    if (activePage === 'home') {
      return <HomePage user={user} dark={dark} platform={platform} onSelectService={setSelectedService}/>;
    }
    if (activePage === 'credits' && showTopup) {
      return <TopupPage user={user} dark={dark} onBack={() => setShowTopup(false)} onSuccess={() => setShowTopup(false)}/>;
    }
    if (activePage === 'credits') return <CreditsPage user={user} dark={dark} onTopup={() => setShowTopup(true)} onUseCredit={(svc) => { setSelectedService(svc); setActivePage('home'); }}/>;
    if (activePage === 'history') return <HistoryPage user={user} dark={dark}/>;
    return null;
  };

  return (
    <ModalProvider dark={dark}>
      <div style={{minHeight:'100vh',background:dark?'#0d0d0d':'#f8f8f8',paddingBottom:'64px',fontFamily:'Prompt,sans-serif'}}>

        <div style={{background:dark?'#161616':'#fff',borderBottom:`0.5px solid ${dark?'#2a2a2a':'#e8e8e8'}`,padding:'10px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:10}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF4500" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C12 2 7 7 7 13C7 15.8 9.2 18 12 18C14.8 18 17 15.8 17 13C17 11 15.5 9.5 15.5 9.5C15.5 9.5 15 12 13 12C13 12 14 10 12 2Z"/>
              <path d="M12 18C10.3 18 9 19.3 9 21H15C15 19.3 13.7 18 12 18Z" opacity="0.6"/>
            </svg>
            <div>
              <p style={{fontSize:'13px',fontWeight:'700',color:dark?'#eee':'#111',margin:0}}>FireSocial<span style={{color:'#FF4500'}}>24</span></p>
              <p style={{fontSize:'10px',color:'#888',margin:0}}>{platform} · ระบบสั่งซื้ออัตโนมัติ</p>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            {user?.pictureUrl && (
              <img src={user.pictureUrl} alt="avatar" style={{width:'30px',height:'30px',borderRadius:'50%'}}/>
            )}
            <button onClick={() => setDark(!dark)} style={{background:'none',border:'none',cursor:'pointer',padding:'4px'}}>
              <i className={`ti ti-${dark?'sun':'moon'}`} style={{fontSize:'20px',color:dark?'#FF4500':'#555'}} aria-hidden="true"/>
            </button>
          </div>
        </div>

        {renderPage()}

        <div style={{position:'fixed',bottom:0,left:0,right:0,background:dark?'#161616':'#fff',borderTop:`0.5px solid ${dark?'#2a2a2a':'#e8e8e8'}`,display:'flex'}}>
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActivePage(item.id); setSelectedService(null); setShowTopup(false); }}
              style={{
                flex:1,padding:'8px 4px',textAlign:'center',cursor:'pointer',
                background:'none',border:'none',
                color:activePage===item.id?'#FF4500':(dark?'#555':'#bbb'),
                fontFamily:'Prompt,sans-serif',fontSize:'10px',fontWeight:'500'
              }}
            >
              <i className={`ti ${item.icon}`} style={{fontSize:'20px',display:'block',marginBottom:'2px'}} aria-hidden="true"/>
              {item.label}
            </button>
          ))}
        </div>

      </div>

      {needsProfile && (
        <ProfileGate user={user} dark={dark} onComplete={() => setNeedsProfile(false)} />
      )}
    </ModalProvider>
  );
}

export default App;